import type { Event } from '../../../types/event';
import { eventPath } from '@constants/routes';
import {
  CardLink,
  CardWrapper,
  CardBackground,
  CardPattern,
  CardGradient,
  CardAccentGlow,
  CardContent,
  CardCategory,
  CardTitle,
  CardTagline,
  CardFooter,
  CardDate,
  CardArrow,
} from './styles';
import { useState } from 'react';

const categoryLabel: Record<string, string> = {
  festival: 'Festival',
  varejo: 'Varejo',
  gastronomico: 'Gastronomia',
  corporativo: 'Corporativo',
  rural: 'Rural',
  exposicao: 'Exposição',
};

const gradientMap: Record<string, string> = {
  navy: 'linear-gradient(135deg, #0C1F2E 0%, #183C54 100%)',
  steel: 'linear-gradient(135deg, #183C54 0%, #186C9C 100%)',
  cyan: 'linear-gradient(135deg, #186C9C 0%, #00A8F0 60%, #3BBAF3 100%)',
};

interface EventCardProps {
  event: Event;
}

/**
 * Card de evento com hover cinematográfico.
 * - Background com gradiente da cor do evento
 * - Pattern de pontos sutil
 * - Glow ciano no hover
 * - Título e tagline animados no hover
 * - Arrow indicator
 */
export function EventCard({ event }: EventCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const useImage = !!event.cover && !imageError;

  return (
    <CardLink to={eventPath(event.slug)} aria-label={`Ver detalhes: ${event.name}`}>
      <CardWrapper
        initial="idle"
        whileHover="hover"
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Pré-carrega a imagem de forma invisível para detectar erro */}
        {event.cover && !imageError && (
          <img
            src={event.cover}
            alt=""
            aria-hidden
            style={{ display: 'none' }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}

        {/* Fundo */}
        <CardBackground $image={event.cover} $color={gradientMap[event.accentColor ?? 'steel']} />
        <CardPattern />
        <CardGradient />

        {/* Glow que aparece no hover */}
        <CardAccentGlow
          variants={{
            idle: { opacity: 0, scale: 0.8 },
            hover: { opacity: 1, scale: 1.2 },
          }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />

        {/* Zoom sutil no fundo no hover */}
        <CardBackground
          $image={useImage && imageLoaded ? event.cover : undefined}
          $color={gradientMap[event.accentColor ?? 'steel']}
          variants={{
            idle: { scale: 1 },
            hover: { scale: 1.04 },
          }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        />

        {/* Conteúdo */}
        <CardContent>
          <CardCategory>{categoryLabel[event.category]}</CardCategory>

          <CardTitle
            variants={{
              idle: { y: 0 },
              hover: { y: -4 },
            }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {event.name}
          </CardTitle>

          <CardTagline
            variants={{
              idle: { opacity: 0.65, y: 0 },
              hover: { opacity: 1, y: -2 },
            }}
            transition={{ duration: 0.3, ease: 'easeOut', delay: 0.04 }}
          >
            {event.tagline}
          </CardTagline>

          <CardFooter
            variants={{
              idle: { opacity: 0.6 },
              hover: { opacity: 1 },
            }}
          >
            <CardDate>{event.dateLabel}</CardDate>
            <CardArrow
              variants={{
                idle: { x: 0 },
                hover: { x: 4 },
              }}
              transition={{ duration: 0.25 }}
            >
              →
            </CardArrow>
          </CardFooter>
        </CardContent>
      </CardWrapper>
    </CardLink>
  );
}
